import type { QueryClient } from '@tanstack/react-query'
import {
  ActivityLogAction,
  CUSTOMER_CONFIG_LABELS,
  type CustomerConfigKey,
} from 'modules/activity-log-service/constants'

/** Toggles the UI shows as ON while the API has no stored value yet. */
const DEFAULT_ON_KEYS: ReadonlySet<CustomerConfigKey> =
  new Set<CustomerConfigKey>([
    'isEnrollmentReminderEmailEnabled',
    'isEnrollmentConfirmationEmailEnabled',
    'isApplicationVerifiedEmailEnabled',
  ])

export const CUSTOMER_TOGGLES_QUERY_KEY = 'toggles'

/**
 * The value the admin saw before the change: the cached toggles value when
 * present, the UI default when unset, otherwise the inverse of the new value
 * (every toggle here is a boolean flip).
 */
export function resolvePreviousValue(
  queryClient: QueryClient,
  customerId: string,
  settingKey: CustomerConfigKey,
  newValue: boolean
): boolean {
  const cached = queryClient.getQueryData<Record<string, unknown>>([
    CUSTOMER_TOGGLES_QUERY_KEY,
    customerId,
  ])
  const cachedValue = cached?.[settingKey]
  if (typeof cachedValue === 'boolean') return cachedValue
  if (DEFAULT_ON_KEYS.has(settingKey)) return true
  return !newValue
}

/**
 * Writes the "Update Customer Configuration" activity entry for one toggle
 * change. Fire-and-forget: a failed log must never fail the toggle itself
 * (`postActivityLog` already swallows and reports its own errors).
 */
export function logCustomerConfigurationChange({
  queryClient,
  customerId,
  companyName,
  settingKey,
  newValue,
}: {
  queryClient: QueryClient
  customerId: string
  companyName?: string
  settingKey: CustomerConfigKey
  newValue: boolean
}) {
  const oldValue = resolvePreviousValue(
    queryClient,
    customerId,
    settingKey,
    newValue
  )
  const settingLabel = CUSTOMER_CONFIG_LABELS[settingKey]
  const customerName = companyName ?? customerId

  // `postActivityLog` pulls in the redux store; load it lazily so importing the
  // configuration hook (e.g. from the nav or census routes) stays light.
  void import('modules/activity-log-service/helpers')
    .then(({ postActivityLog }) =>
      postActivityLog({
        action: ActivityLogAction.UPDATE_CUSTOMER_CONFIGURATION,
        description: `turned ${newValue ? 'ON' : 'OFF'} ${settingLabel} for customer ${customerName}`,
        targetUserId: customerId,
        targetName: companyName,
        targetType: 'COMPANY',
        additionalData: {
          currentCompany: { id: customerId, name: companyName },
          settingKey,
          settingLabel,
          oldData: { [settingKey]: oldValue },
          newData: { [settingKey]: newValue },
        },
      })
    )
    // Make the history list on the page pick up the new entry.
    .then(() => queryClient.invalidateQueries({ queryKey: ['activityLogs'] }))
    .catch((error) =>
      console.error('Failed to log configuration change', error)
    )
}
